import { Link } from "react-router-dom";
import { useFacility, splitFacilityName } from "./context/FacilityContext";

export default function Hero() {
  const { facility } = useFacility();
  const [firstWord, restName] = splitFacilityName(facility?.name);
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@200;300;400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .hero-root {
          min-height: 100vh;
          position: relative;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          font-family: 'Jost', sans-serif;
          background: #1a110a;
        }

        /* ── BACKGROUND LAYERS ── */
        .hero-bg {
          position: absolute;
          inset: 0;
          background-image: url('https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=1600&q=80');
          background-size: cover;
          background-position: center 40%;
          animation: slowZoom 18s ease-in-out infinite alternate;
        }

        .hero-overlay-base {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            115deg,
            rgba(20,10,5,.92) 0%,
            rgba(28,16,8,.75) 40%,
            rgba(15,8,3,.45) 100%
          );
        }

        .hero-overlay-vignette {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 60% 80% at 15% 50%, rgba(200,160,90,.06) 0%, transparent 70%),
            radial-gradient(ellipse 100% 40% at 50% 100%, rgba(10,5,2,.9) 0%, transparent 60%),
            radial-gradient(ellipse 50% 30% at 50% 0%, rgba(10,5,2,.6) 0%, transparent 50%);
        }

        /* grain texture */
        .hero-grain {
          position: absolute;
          inset: 0;
          opacity: 0.035;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
          background-size: 180px;
        }

        /* animated shimmer line */
        .hero-shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(200,160,90,.04), transparent);
          animation: shimmer 8s ease-in-out 1.5s infinite;
          pointer-events: none;
        }

        /* ── NAV ── */
        .hero-nav {
          position: relative;
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 28px 56px;
          animation: fadeDown 0.8s 0.2s both;
        }

        .nav-logo {
          display: flex;
          align-items: center;
          gap: 14px;
          text-decoration: none;
        }

        .nav-logo-mark {
          width: 38px;
          height: 38px;
          border: 1.5px solid rgba(200,160,90,.6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .nav-logo-text {
          font-family: 'Cormorant Garamond', serif;
          font-size: 18px;
          font-weight: 400;
          color: #f0e4cf;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .nav-logo-text span {
          color: #c8a05a;
          font-style: italic;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 36px;
        }

        .nav-link {
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(240,228,207,.55);
          text-decoration: none;
          transition: color 0.2s;
          position: relative;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -3px;
          left: 0;
          width: 0;
          height: 1px;
          background: #c8a05a;
          transition: width 0.3s;
        }

        .nav-link:hover { color: rgba(240,228,207,.9); }
        .nav-link:hover::after { width: 100%; }

        /* ── MAIN CONTENT ── */
        .hero-body {
          position: relative;
          z-index: 10;
          flex: 1;
          display: flex;
          align-items: center;
          padding: 0 56px;
        }

        .hero-content {
          max-width: 800px;
        }

        .hero-eyebrow {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 24px;
          animation: fadeUp 0.7s 0.4s both;
        }

        .eyebrow-line {
          width: 36px;
          height: 1px;
          background: linear-gradient(90deg, #c8a05a, rgba(200,160,90,.3));
        }

        .eyebrow-text {
          font-size: 10.5px;
          font-weight: 400;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #c8a05a;
        }

        .hero-title {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 300;
          font-size: clamp(54px, 7.5vw, 108px);
          line-height: 0.95;
          color: #f0e4cf;
          letter-spacing: -0.02em;
          margin-bottom: 6px;
          animation: fadeUp 0.9s 0.5s both;
        }

        .hero-title-em {
          display: block;
          font-style: italic;
          color: #c8a05a;
          font-weight: 300;
        }

        .hero-title-sub {
          display: block;
          font-weight: 600;
          font-size: clamp(20px, 2.8vw, 38px);
          color: rgba(240,228,207,.5);
          letter-spacing: 0.25em;
          text-transform: uppercase;
          font-style: normal;
          font-family: 'Jost', sans-serif;
          font-weight: 200;
          margin-top: 10px;
          animation: fadeUp 0.9s 0.65s both;
        }

        .hero-divider {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 32px 0;
          animation: fadeUp 0.7s 0.75s both;
        }

        .divider-line {
          flex: 0 0 60px;
          height: 1px;
          background: linear-gradient(90deg, rgba(200,160,90,.6), rgba(200,160,90,.1));
        }

        .divider-diamond {
          width: 5px;
          height: 5px;
          background: #c8a05a;
          transform: rotate(45deg);
          opacity: 0.7;
        }

        .hero-desc {
          font-size: 15px;
          font-weight: 300;
          line-height: 1.75;
          color: rgba(220,205,185,.65);
          max-width: 440px;
          letter-spacing: 0.03em;
          animation: fadeUp 0.7s 0.85s both;
        }

        .hero-cta {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          margin-top: 44px;
          animation: fadeUp 0.7s 0.95s both;
        }

        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 38px;
          background: #c8a05a;
          color: #1a110a;
          border: none;
          border-radius: 3px;
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          text-decoration: none;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: background 0.25s, transform 0.15s;
        }

        .btn-primary::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,.15), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .btn-primary:hover { background: #d4ae6a; transform: translateY(-2px); }
        .btn-primary:hover::before { opacity: 1; }
        .btn-primary:active { transform: translateY(0); }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 15px 38px;
          background: transparent;
          color: rgba(240,228,207,.8);
          border: 1px solid rgba(200,160,90,.35);
          border-radius: 3px;
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          text-decoration: none;
          transition: border-color 0.25s, color 0.25s, transform 0.15s;
        }

        .btn-secondary:hover {
          border-color: rgba(200,160,90,.7);
          color: #f0e4cf;
          transform: translateY(-2px);
        }

        /* ── STATS BAR ── */
        .hero-stats {
          position: relative;
          z-index: 10;
          display: flex;
          align-items: stretch;
          border-top: 1px solid rgba(200,160,90,.12);
          animation: fadeUp 0.7s 1.1s both;
        }

        .stat-item {
          flex: 1;
          padding: 22px 32px;
          border-right: 1px solid rgba(200,160,90,.1);
          display: flex;
          align-items: center;
          gap: 16px;
          transition: background 0.3s;
        }

        .stat-item:last-child { border-right: none; }
        .stat-item:hover { background: rgba(200,160,90,.04); }

        .stat-icon {
          width: 34px;
          height: 34px;
          border: 1px solid rgba(200,160,90,.25);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #c8a05a;
        }

        .stat-number {
          font-family: 'Cormorant Garamond', serif;
          font-size: 24px;
          font-weight: 300;
          color: #f0e4cf;
          line-height: 1;
        }

        .stat-label {
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(200,175,145,.45);
          margin-top: 3px;
        }

        /* ── SCROLL INDICATOR ── */
        .scroll-indicator {
          position: absolute;
          right: 52px;
          bottom: 90px;
          z-index: 10;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          animation: fadeUp 0.7s 1.3s both;
        }

        .scroll-text {
          writing-mode: vertical-rl;
          font-size: 9px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(200,160,90,.45);
        }

        .scroll-line {
          width: 1px;
          height: 50px;
          background: linear-gradient(to bottom, rgba(200,160,90,.4), transparent);
          animation: scrollPulse 2s ease-in-out 2s infinite;
        }

        /* ── ANIMATIONS ── */
        @keyframes slowZoom {
          from { transform: scale(1); }
          to   { transform: scale(1.06); }
        }
        @keyframes shimmer {
          0%   { left: -40%; }
          50%  { left: 130%; }
          100% { left: 130%; }
        }
        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes scrollPulse {
          0%, 100% { opacity: 0.4; transform: scaleY(1); }
          50%       { opacity: 1;   transform: scaleY(1.15); }
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .hero-nav  { padding: 20px 24px; }
          .nav-links { display: none; }
          .hero-body { padding: 0 24px; }
          .hero-stats { flex-direction: column; }
          .stat-item { border-right: none; border-bottom: 1px solid rgba(200,160,90,.1); padding: 16px 24px; }
          .stat-item:last-child { border-bottom: none; }
          .scroll-indicator { display: none; }
        }
      `}</style>

      <div className="hero-root">
        {/* Background */}
        <div className="hero-bg" />
        <div className="hero-overlay-base" />
        <div className="hero-overlay-vignette" />
        <div className="hero-grain" />
        <div className="hero-shimmer" />

       

        {/* Body */}
        <div className="hero-body">
          <div className="hero-content">
            <div className="hero-eyebrow">
              <div className="eyebrow-line" />
              <span className="eyebrow-text">Premium Hospitality Experience</span>
            </div>

            <h1 className="hero-title">
              {firstWord
                ? <em className="hero-title-em">{firstWord}</em>
                : <em className="hero-title-em">Hotel</em>}
              {restName ? <>{" "}{restName}</> : null}
            </h1>
            <p className="hero-title-sub">
              {facility?.location
                ? `${facility.location}${facility.region ? `, ${facility.region}` : ""}`
                : "Home away from Home"}
            </p>

            <div className="hero-divider">
              <div className="divider-line" />
              <div className="divider-diamond" />
            </div>

            <p className="hero-desc">
              Where refined elegance meets heartfelt comfort. Discover a sanctuary crafted for the
              discerning traveller — premium rooms, warm hospitality, and a stay you will remember.
            </p>

            <div className="hero-cta">
              <Link to="/reserve" className="btn-primary">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Reserve a Room
              </Link>
              <Link to="/login" className="btn-secondary">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                </svg>
                Staff Login
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator">
          <span className="scroll-text">Scroll</span>
          <div className="scroll-line" />
        </div>

        {/* Stats Bar */}
        <div className="hero-stats">
          {[
            {
              icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              ),
              num: "500+",
              label: "Happy Guests",
            },
            {
              icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              ),
              num: "24",
              label: "Premium Rooms",
            },
            {
              icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              ),
              num: "24 / 7",
              label: "Concierge Service",
            },
            {
              icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              ),
              num: "4.9★",
              label: "Guest Rating",
            },
          ].map((s, i) => (
            <div className="stat-item" key={i}>
              <div className="stat-icon">{s.icon}</div>
              <div>
                <div className="stat-number">{s.num}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}